/**
 * A peninsula's back in STONE.
 *
 * When the counters over a peninsula's back turn down as waterfalls, the back
 * between them is stone too — one stone panel across the whole back, never the
 * wood the arm's cabinets wear (user, 2026-10-29; Jerry's House read stone,
 * wood, stone along its back). The back panel keeps its place and size; what
 * changes is what it is cut from: the stone of the waterfall counter over the
 * most of it.
 *
 * A back runs under several counters (the arm's pieces, the bridge behind a
 * range, the seated slab at a floating L's corner), and an end finish is set
 * per counter. The back follows the counters over MOST of its length: stone
 * when more than half of it is under waterfall counters, wood otherwise — so
 * a waterfall picked for one short piece does not turn a whole back to stone,
 * and one piece set back to plain does not turn it to wood.
 *
 * Only where the back is the FACE under the counter's edge. A seated arm's
 * back stands under the bar's knee overhang, where people sit — not the face
 * anyone sees across the room — and stays wood however the slab over it
 * finishes (user, 2026-10-29: the underside of Jerry's bar turned stone). A
 * counter overhangs a back when its edge stands past the back's face by more
 * than its own thickness: the one counter edge that stands proud of a back by
 * design is a seated top that reached a leg's thickness past the other arm's
 * back before 2026-10-29 (`seatedPeninsulaTop.reachOpenEnds`), and that is the
 * back's edge; a bar's knee overhang is 12". An old seated arm's separate knee
 * strip, outboard of a flush top, overhangs the back the same way.
 *
 * Read when the room is drawn and billed, like an end's finish
 * (`countertopEnds`), so it follows a counter's finish however that is
 * changed. The renderer draws the panel in the counter's stone
 * (`blankPanelRenderer`, through `proceduralRenderOptions`); the estimate bills
 * its area as that counter's waterfall stone and drops the wood panel it
 * replaces (`countertopFabrication.stoneBackAreaSqM`, `collectCabinetUnits`).
 * Pure.
 */

const { isPeninsulaBackPanel } = require('./peninsulaBack');
const { countertopEndFinish } = require('./countertopEnds');
const { objectFootprint } = require('../geometry/objectFootprint');

/** A top's underside within this of the panel's top stands on the panel. */
const SEAT_TOL_M = 0.03;
/** Slack on "no more than its own thickness past the back's face". */
const REACH_TOL_M = 0.005;
const EPS = 1e-6;

const isCounter = (o) =>
  (o?.type === 'countertop' || o?.category?.countertop === true) &&
  o.center &&
  o.dimensions;

const outlineOf = (o) =>
  Array.isArray(o.polygonCorners) && o.polygonCorners.length >= 3
    ? o.polygonCorners
    : objectFootprint(o);

/**
 * How a counter stands over a panel, or null when it does not stand on it
 * (its underside not at the panel's top) or runs past none of it:
 *
 *   - `span`       `[a0, a1]`, the stretch along the panel, from its centre;
 *   - `covers`     it reaches across the panel's centre line — it is a top
 *                  OVER the back;
 *   - `overhangs`  its edge stands past the back's face by more than its own
 *                  thickness, from the back (a bar's knee overhang, or a knee
 *                  strip outboard of the back).
 */
function standingOver(panel, top) {
  const panelTop = panel.center.y + panel.dimensions.y / 2;
  const thick = top.dimensions.y || 0;
  const underside = top.center.y - thick / 2;
  if (
    Number.isFinite(panelTop) &&
    Number.isFinite(underside) &&
    Math.abs(underside - panelTop) > SEAT_TOL_M
  ) {
    return null;
  }
  const outline = outlineOf(top);
  if (!outline) return null;
  const ry = panel.rotation?.y || 0;
  const d = { x: Math.cos(ry), z: -Math.sin(ry) };
  // the panel's finished face looks out along its local +Z
  const n = { x: Math.sin(ry), z: Math.cos(ry) };
  const pts = outline.map((p) => ({
    a: (p.x - panel.center.x) * d.x + (p.z - panel.center.z) * d.z,
    n: (p.x - panel.center.x) * n.x + (p.z - panel.center.z) * n.z,
  }));
  const half = panel.dimensions.x / 2;
  const a0 = Math.max(-half, Math.min(...pts.map((p) => p.a)));
  const a1 = Math.min(half, Math.max(...pts.map((p) => p.a)));
  if (!(a1 - a0 > EPS)) return null;
  const face = panel.dimensions.z / 2;
  const reach = face + thick + REACH_TOL_M;
  const nMin = Math.min(...pts.map((p) => p.n));
  const nMax = Math.max(...pts.map((p) => p.n));
  return {
    span: [a0, a1],
    covers: nMin < -EPS && nMax > EPS,
    overhangs: nMax > reach && nMin <= reach,
  };
}

/** `spans` merged where they touch or overlap, in order. */
function merged(spans) {
  const out = [];
  for (const [a0, a1] of [...spans].sort((x, y) => x[0] - y[0])) {
    const last = out[out.length - 1];
    if (last && a0 <= last[1]) last[1] = Math.max(last[1], a1);
    else out.push([a0, a1]);
  }
  return out;
}

/** The length of `spans` (their union) outside `holes` (theirs). */
function lengthOutside(spans, holes) {
  const keep = merged(spans);
  const cut = merged(holes);
  let total = 0;
  for (const [a0, a1] of keep) {
    let len = a1 - a0;
    for (const [b0, b1] of cut)
      len -= Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
    total += len;
  }
  return total;
}

/**
 * The waterfall counter whose stone a peninsula back panel is cut from, or
 * null when the panel stays wood: not a peninsula's back, or less than half
 * its length under waterfall counters it is the face of (a stretch under a
 * bar's overhang never counts). Of the waterfall counters over it, the one
 * covering the most of it.
 *
 * @param {Object} panel
 * @param {{ objects?: Object[], cabinetRuns?: Object[] }} ctx the room (a room
 *   document is one) — `countertopEndFinish` reads the runs for a top's
 *   default finish
 * @returns {Object|null}
 */
function stoneBackTop(panel, ctx = {}) {
  if (!isPeninsulaBackPanel(panel)) return null;
  if (!panel.center || !(panel.dimensions?.x > 0)) return null;
  const waterfalls = [];
  const bars = [];
  for (const top of ctx.objects || []) {
    if (!isCounter(top)) continue;
    const over = standingOver(panel, top);
    if (!over) continue;
    if (over.overhangs) {
      bars.push(over.span);
      continue;
    }
    if (!over.covers) continue;
    if (countertopEndFinish(top, ctx) !== 'waterfall') continue;
    waterfalls.push({ top, len: over.span[1] - over.span[0], span: over.span });
  }
  if (!waterfalls.length) return null;
  const stone = lengthOutside(
    waterfalls.map((w) => w.span),
    bars
  );
  if (!(stone > panel.dimensions.x / 2)) return null;
  return waterfalls.reduce((best, w) => (w.len > best.len ? w : best)).top;
}

/**
 * The peninsula back panels cut from `top`'s stone (`stoneBackTop`).
 *
 * @param {Object} top
 * @param {{ objects?: Object[], cabinetRuns?: Object[] }} ctx
 * @returns {Object[]}
 */
function stoneBacksOf(top, ctx = {}) {
  if (!top?.id) return [];
  return (ctx.objects || []).filter(
    (o) => isPeninsulaBackPanel(o) && stoneBackTop(o, ctx)?.id === top.id
  );
}

module.exports = { stoneBackTop, stoneBacksOf };
